import React, { useState, useContext, useEffect, useMemo, useRef } from 'react';
import { Modal } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import { useDropzone, FileWithPath } from 'react-dropzone';
import * as XLSX from 'xlsx';
import { enqueueSnackbar } from 'notistack';
import { GlobalContext } from '../../providers/GlobalProvider';
import { useLogAppAction } from 'hooks/useLogAppAction';
import {
  ExportHubAPI,
  ExportDatabase,
  ExportDataSource,
  CreateExportProductInput,
  ExportProductTier,
  ExportJurisdiction,
  ExportComplianceStatus,
  ExportScreeningFrequency,
  ExportNotificationMethod,
  EXPORT_COMMODITY_LENSES,
} from 'api/ExportHubAPI';
import ScreeningSettingsFields from './ScreeningSettingsFields';
import StyledSingleSelect from './StyledSingleSelect';
import { fetchExportCommodityLenses } from './screeningLenses';
import {
  DATABASE_ICON_KEYS,
  getDatabaseIcon,
  DEFAULT_DATABASE_ICON,
  ACCENT_PALETTE,
  DEFAULT_ACCENT,
} from './databasePresets';
import {
  PRODUCT_TEMPLATE_COLUMNS,
  PRODUCT_TEMPLATE_EXAMPLE_ROWS,
  PRODUCT_PARENT_COLUMN,
  PRODUCT_BOM_QUANTITY_COLUMN,
  TIER_OPTIONS,
  JURISDICTION_OPTIONS,
  STATUS_OPTIONS,
  TierBadge,
} from './productPresets';
import { buildBomPlan, BomPlanRow, BomPlanNode } from './bomImportPlan';
import { csvJSON } from '../../utils/CsvToJson';
import {
  checkProductCredits,
  type ProductCreditCheck,
} from 'utils/productCredits';
import ProductCreditsModal from 'pages/import/ProductCreditsModal';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (db: ExportDatabase) => void;
  /** Called instead of onCreated when startMode === 'documents': the caller
   * hands off into the embedded document-upload view for this database
   * rather than the normal post-create navigation into its dashboard. */
  onCreatedForDocumentUpload?: (db: ExportDatabase, file: File) => void;
}

type StartMode = 'manual' | 'file' | 'documents';
const NOT_MAPPED = 'not_mapped';
// The Structure step previews rather than lists. Capping roots alone isn't
// enough — one root can carry thousands of descendants and renderPlanNode
// recurses through all of them — so the budget is counted across the whole
// traversal, not per root.
const TREE_PREVIEW_CAP = 25;
const TREE_PREVIEW_NODE_BUDGET = 300;
const ISSUE_PREVIEW_CAP = 40;

// --- CSV helpers ------------------------------------------------------------

const normalizeHeader = (h: string): string =>
  h.trim().toLowerCase().replace(/\s+/g, '_');

const csvCell = (v: unknown): string => {
  const s = `${v ?? ''}`.replace(/"/g, '""');
  return `"${s}"`;
};

const downloadCsv = (filename: string, columns: string[], rows: Record<string, string>[]) => {
  const lines = [columns.join(',')];
  rows.forEach((r) => lines.push(columns.map((c) => csvCell(r[c])).join(',')));
  const blob = new Blob([lines.join('\r\n')], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const parseBool = (v: string): boolean =>
  /^(y|yes|true|1)$/i.test((v || '').trim());

const asEnum = <T extends string>(v: string, allowed: T[]): T | undefined => {
  const t = (v || '').trim();
  return allowed.find((a) => a.toLowerCase() === t.toLowerCase());
};

const CreateDatabaseModal: React.FC<Props> = ({
  open,
  onClose,
  onCreated,
  onCreatedForDocumentUpload,
}) => {
  const { cur_user } = useContext(GlobalContext);
  const groupId = cur_user?.selected_team?.id;
  const logAppAction = useLogAppAction();

  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState(DEFAULT_DATABASE_ICON);
  const [accent, setAccent] = useState(DEFAULT_ACCENT);
  const [startMode, setStartMode] = useState<StartMode>('manual');

  // Screening settings (applied to the new database on create)
  const [frequency, setFrequency] = useState<ExportScreeningFrequency>('');
  const [dayOfWeek, setDayOfWeek] = useState('Monday');
  const [notifyMethod, setNotifyMethod] =
    useState<ExportNotificationMethod>('in_app');
  const [notifyEmail, setNotifyEmail] = useState('');
  const [availableLenses, setAvailableLenses] = useState<string[]>([]);
  const [lenses, setLenses] = useState<string[]>([]);
  const [lensesLoading, setLensesLoading] = useState(true);

  // CSV import state
  const [file, setFile] = useState<File | null>(null);
  const [rawRows, setRawRows] = useState<Record<string, string>[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [mappings, setMappings] = useState<Record<string, string>>({});

  // "Upload documents" start mode: the customs-entry PDF attached here rides
  // along in router state and lands already staged in the Customs Entry
  // Analyzer's own composer -- see the 'Documents' step below and handleSubmit.
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [documentDragActive, setDocumentDragActive] = useState(false);
  const documentFileInputRef = useRef<HTMLInputElement>(null);

  const [saving, setSaving] = useState(false);
  const [creditBlock, setCreditBlock] = useState<ProductCreditCheck | null>(
    null,
  );
  const [importMsg, setImportMsg] = useState('');

  // Stepper is dynamic: the CSV branch adds Template + Match + Structure. All
  // three sit before Settings so the file is in hand — and its hierarchy
  // confirmed — before the database is configured.
  const steps = useMemo(
    () =>
      startMode === 'file'
        ? [
            'Details',
            'Appearance',
            'Products',
            'Template',
            'Match',
            'Structure',
            'Settings',
          ]
        : startMode === 'documents'
        ? ['Details', 'Appearance', 'Products', 'Documents', 'Settings']
        : ['Details', 'Appearance', 'Products', 'Settings'],
    [startMode],
  );
  const lastStep = steps.length - 1;

  // Load the available BITE lenses once (same source as Export Screening) and
  // default to screening against all of them.
  useEffect(() => {
    let active = true;
    setLensesLoading(true);
    fetchExportCommodityLenses()
      .then((avail) => {
        if (!active) return;
        const options = avail.length ? avail : [...EXPORT_COMMODITY_LENSES];
        setAvailableLenses(options);
        setLenses([...options]);
      })
      .finally(() => active && setLensesLoading(false));
    return () => {
      active = false;
    };
  }, []);

  // Seed the notification email from the current user (only used if they pick
  // Email/Both).
  useEffect(() => {
    if (cur_user?.basics?.email) setNotifyEmail(cur_user.basics.email);
  }, [cur_user?.basics?.email]);

  const reset = () => {
    setStep(0);
    setName('');
    setDescription('');
    setIcon(DEFAULT_DATABASE_ICON);
    setAccent(DEFAULT_ACCENT);
    setStartMode('manual');
    setFrequency('');
    setDayOfWeek('Monday');
    setNotifyMethod('in_app');
    setNotifyEmail(cur_user?.basics?.email || '');
    setLenses([...availableLenses]);
    setFile(null);
    setRawRows([]);
    setHeaders([]);
    setMappings({});
    setImportMsg('');
    setDocumentFile(null);
    setDocumentDragActive(false);
  };

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose();
  };

  // --- CSV upload / parse ---------------------------------------------------

  const autoMap = (hs: string[]): Record<string, string> => {
    const used = new Set<string>();
    const out: Record<string, string> = {};
    hs.forEach((h) => {
      const norm = normalizeHeader(h);
      const match = PRODUCT_TEMPLATE_COLUMNS.find(
        (c) => (c === h || c === norm) && !used.has(c),
      );
      if (match) {
        out[h] = match;
        used.add(match);
      } else {
        out[h] = NOT_MAPPED;
      }
    });
    return out;
  };

  const ingestRows = (rows: Record<string, string>[]) => {
    const hs = Object.keys(rows[0] || {});
    setRawRows(rows);
    setHeaders(hs);
    setMappings(autoMap(hs));
  };

  const parseFile = async (f: File) => {
    if (f.name.endsWith('.csv') || f.type === 'text/csv') {
      const text = await f.text();
      ingestRows(csvJSON(text));
    } else if (f.name.endsWith('.xlsx')) {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      ingestRows(XLSX.utils.sheet_to_json(ws, { defval: '' }) as Record<string, string>[]);
    } else {
      throw new Error('Unsupported file. Upload a .csv or .xlsx file.');
    }
  };

  const onDrop = async (accepted: FileWithPath[]) => {
    // FileWithPath extends File; cast keeps parseFile/setFile typed as DOM File.
    const f = accepted[0] as File | undefined;
    if (!f) return;
    try {
      await parseFile(f);
      setFile(f);
      enqueueSnackbar('File ready — review the column matches.', {
        variant: 'success',
      });
    } catch (e) {
      enqueueSnackbar(
        e instanceof Error ? e.message : 'Could not read that file.',
        { variant: 'error' },
      );
      setFile(null);
    }
  };

  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({
      onDrop: onDrop,
      accept: [
        'text/csv',
        '.csv',
        '.xlsx',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ],
      multiple: false,
    });

  // Parse the mapped upload once per file/mapping change into (a) the product
  // rows to create and (b) the parent/child tree their `parent_sku` column
  // describes. Both are index-aligned, so a row's created id is also its node
  // in the plan.
  const importPlan = useMemo(() => {
    if (!groupId) return null;
    const fieldFor = (row: Record<string, string>, col: string): string => {
      const header = headers.find((h) => mappings[h] === col);
      return header ? `${row[header] ?? ''}`.trim() : '';
    };
    const inputs: CreateExportProductInput[] = [];
    const planRows: BomPlanRow[] = [];
    rawRows.forEach((row, idx) => {
      const name_ = fieldFor(row, 'name');
      if (!name_) return; // name is required — skip blank rows
      const destinations = fieldFor(row, 'intended_destination_countries')
        .split(/[;,]/)
        .map((s) => s.trim())
        .filter(Boolean);
      const qtyRaw = fieldFor(row, 'quantity');
      const qty = qtyRaw ? Math.max(0, Math.floor(Number(qtyRaw))) : undefined;
      const bomQtyRaw = fieldFor(row, PRODUCT_BOM_QUANTITY_COLUMN);
      const bomQty = bomQtyRaw
        ? Math.max(0, Math.floor(Number(bomQtyRaw)))
        : undefined;
      const sku = fieldFor(row, 'internal_sku');
      inputs.push({
        group_id: groupId,
        name: name_,
        internal_sku: sku || undefined,
        description: fieldFor(row, 'description') || undefined,
        manufacturer: fieldFor(row, 'manufacturer') || undefined,
        country_of_origin: fieldFor(row, 'country_of_origin') || undefined,
        materials: fieldFor(row, 'materials') || undefined,
        intended_end_use: fieldFor(row, 'intended_end_use') || undefined,
        hardware_authorized: parseBool(fieldFor(row, 'hardware_authorized')),
        tech_data_authorized: parseBool(fieldFor(row, 'tech_data_authorized')),
        quantity: Number.isFinite(qty as number) ? qty : undefined,
        unit_of_measure: fieldFor(row, 'unit_of_measure') || undefined,
        hts_code: fieldFor(row, 'hts_code') || undefined,
        eccn: fieldFor(row, 'eccn') || undefined,
        intended_destination_countries: destinations,
        jurisdiction: asEnum<ExportJurisdiction>(
          fieldFor(row, 'jurisdiction'),
          JURISDICTION_OPTIONS,
        ),
        compliance_status: asEnum<ExportComplianceStatus>(
          fieldFor(row, 'compliance_status'),
          STATUS_OPTIONS,
        ),
      });
      planRows.push({
        rowNumber: idx + 1,
        name: name_,
        sku: sku,
        parentRef: fieldFor(row, PRODUCT_PARENT_COLUMN),
        tier: asEnum<ExportProductTier>(fieldFor(row, 'tier'), TIER_OPTIONS),
        bomQuantity: Number.isFinite(bomQty as number) ? bomQty : undefined,
      });
    });
    const plan = buildBomPlan(planRows);
    // The plan settles every row's tier — read from the file, inferred from the
    // links where the column was blank, or the Component default.
    const products = inputs.map((input, i) => ({
      ...input,
      tier: plan.tiers[i],
    }));
    return { products: products, rows: planRows, plan: plan };
  }, [groupId, rawRows, headers, mappings]);

  // --- Submit ---------------------------------------------------------------

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (!groupId) {
      enqueueSnackbar('No active team selected.', { variant: 'error' });
      return;
    }
    logAppAction('export_hub:create_database');

    // Export Hub products draw on the same product-credit pool as the Import
    // Hub. Checked before the database is created, so a rejected import can't
    // leave an empty database behind.
    const plannedProducts =
      startMode === 'file' ? importPlan?.products.length ?? 0 : 0;
    if (plannedProducts > 0) {
      try {
        const check = await checkProductCredits(groupId, plannedProducts);
        if (!check.ok) {
          setCreditBlock(check);
          return;
        }
      } catch (err) {
        console.error('Product credit check failed:', err);
        enqueueSnackbar(
          'Could not verify your product allowance. Please try again.',
          { variant: 'error' },
        );
        return;
      }
    }

    setSaving(true);
    try {
      const dataSource: ExportDataSource =
        startMode === 'file' ? 'file' : 'manual';
      const db = await ExportHubAPI.createDatabase({
        name: trimmed,
        description: description.trim() || undefined,
        icon: icon,
        accent_color: accent,
        visibility: 'Shared', // all Export Hub databases are team-shared
        data_source: dataSource,
        group_id: groupId,
      });

      // Persist the chosen screening settings on the new database. Non-fatal:
      // the database already exists if this call fails.
      try {
        const emailNeeded =
          notifyMethod === 'email' || notifyMethod === 'both';
        await ExportHubAPI.updateDatabaseSettings(db.id, {
          group_id: groupId,
          screening_frequency: frequency,
          screening_day_of_week: frequency === 'Weekly' ? dayOfWeek : '',
          notification_method: notifyMethod,
          notifications_email: emailNeeded ? notifyEmail.trim() : '',
          // All selected = screen against everything (null); a subset narrows.
          screening_lenses:
            lenses.length === availableLenses.length ? null : lenses,
        });
      } catch (e) {
        console.error('Failed to save screening settings:', e);
        enqueueSnackbar(
          'Database created, but screening settings could not be saved. Adjust them in the dashboard.',
          { variant: 'warning' },
        );
      }

      // If importing, create the parsed products into the new database.
      if (startMode === 'file') {
        const products = importPlan?.products ?? [];
        if (products.length) {
          // Create every row first so both ends of a link exist before we make
          // it, keeping the created ids index-aligned with the plan.
          const createdIds: (string | null)[] = products.map(() => null);
          let ok = 0;
          let failed = 0;
          for (let i = 0; i < products.length; i++) {
            setImportMsg(`Importing products… ${i + 1} of ${products.length}`);
            try {
              // eslint-disable-next-line no-await-in-loop
              const created = await ExportHubAPI.createProduct(
                db.id,
                products[i],
              );
              createdIds[i] = created?.id ?? null;
              ok += 1;
            } catch (e) {
              failed += 1;
              console.error('Product import failed:', e);
            }
          }

          // Then replay the resolved hierarchy. The plan already dropped the
          // links the server would reject, so these are the survivors.
          const edges = (importPlan?.plan.parentIndex ?? [])
            .map((parentIdx, childIdx) => ({
              parent: parentIdx,
              child: childIdx,
            }))
            .filter((e) => e.parent !== -1);
          let linked = 0;
          let linkFailed = 0;
          for (let i = 0; i < edges.length; i++) {
            const parentId = createdIds[edges[i].parent];
            const childId = createdIds[edges[i].child];
            if (!parentId || !childId) {
              linkFailed += 1; // one end of the link never imported
              continue;
            }
            setImportMsg(`Linking structure… ${i + 1} of ${edges.length}`);
            try {
              // eslint-disable-next-line no-await-in-loop
              await ExportHubAPI.addBomLink({
                group_id: groupId,
                parent_product_id: parentId,
                child_product_id: childId,
                quantity: importPlan?.rows[edges[i].child].bomQuantity,
              });
              linked += 1;
            } catch (e) {
              linkFailed += 1;
              console.error('BOM link failed:', e);
            }
          }

          const parts = [`imported ${ok} product${ok === 1 ? '' : 's'}`];
          if (failed) parts.push(`${failed} row${failed === 1 ? '' : 's'} failed`);
          if (linked) parts.push(`linked ${linked} into the hierarchy`);
          if (linkFailed)
            parts.push(`${linkFailed} link${linkFailed === 1 ? '' : 's'} failed`);
          enqueueSnackbar(`Database created — ${parts.join(', ')}.`, {
            variant: failed === 0 && linkFailed === 0 ? 'success' : 'warning',
          });
          // Auto-generate the results we can for the freshly imported products.
          if (ok > 0 && groupId) {
            ExportHubAPI.startClassification(db.id, groupId).catch(
              () => undefined,
            );
          }
        } else {
          enqueueSnackbar('Database created — no valid product rows found.', {
            variant: 'warning',
          });
        }
      } else {
        enqueueSnackbar('Database created.', { variant: 'success' });
      }

      const documentFileToHandOff = documentFile;
      reset();
      if (startMode === 'documents' && documentFileToHandOff) {
        onCreatedForDocumentUpload?.(db, documentFileToHandOff);
      } else {
        onCreated(db);
      }
    } catch (err) {
      console.error('Failed to create database:', err);
      enqueueSnackbar('Failed to create database. Please try again.', {
        variant: 'error',
      });
    } finally {
      setSaving(false);
      setImportMsg('');
    }
  };

  const settingsEmailNeeded =
    notifyMethod === 'email' || notifyMethod === 'both';

  // Per-step validation gating the Continue button.
  const canAdvance = (): boolean => {
    if (step === 0) return !!name.trim();
    if (steps[step] === 'Settings') {
      if (lenses.length === 0) return false;
      if (settingsEmailNeeded && !notifyEmail.trim()) return false;
      return true;
    }
    if (steps[step] === 'Template') return !!file && rawRows.length > 0;
    if (steps[step] === 'Documents') return !!documentFile;
    return true;
  };

  const next = () => {
    if (!canAdvance()) {
      if (step === 0)
        enqueueSnackbar('Enter a database name.', { variant: 'warning' });
      else if (steps[step] === 'Settings' && lenses.length === 0)
        enqueueSnackbar('Select at least one BITE Lens to screen against.', {
          variant: 'warning',
        });
      else if (steps[step] === 'Settings')
        enqueueSnackbar('Enter a notification email address.', {
          variant: 'warning',
        });
      else if (steps[step] === 'Template')
        enqueueSnackbar('Upload a CSV/XLSX to continue.', {
          variant: 'warning',
        });
      else if (steps[step] === 'Documents')
        enqueueSnackbar('Attach a customs entry PDF to continue.', {
          variant: 'warning',
        });
      return;
    }
    setStep((s) => Math.min(lastStep, s + 1));
  };

  const current = steps[step];
  const mappedCount = headers.filter((h) => mappings[h] !== NOT_MAPPED).length;
  const productRowCount = importPlan?.products.length ?? 0;
  const plan = importPlan?.plan;
  const planRows = importPlan?.rows ?? [];
  const parentMapped = headers.some(
    (h) => mappings[h] === PRODUCT_PARENT_COLUMN,
  );

  // Nodes the shown roots would render if unbounded — drives the truncation
  // note, since the budget is spent silently inside the recursion.
  const previewNodeCount = (plan?.roots ?? [])
    .slice(0, TREE_PREVIEW_CAP)
    .reduce((n, root) => {
      const count = (node: BomPlanNode): number =>
        1 + node.children.reduce((m, c) => m + count(c), 0);
      return n + count(root);
    }, 0);

  // One line of the Structure preview: the product, the tier it will be created
  // with, and how many of it go into its parent.
  const renderPlanNode = (
    node: BomPlanNode,
    depth: number,
    budget: { left: number },
  ): React.ReactNode => {
    const row = planRows[node.index];
    if (!row || !plan || budget.left <= 0) return null;
    budget.left -= 1;
    return (
      <React.Fragment key={node.index}>
        <div className='eh-bom-node' style={{ paddingLeft: 12 + depth * 20 }}>
          <span className='eh-bom-node-name'>{row.name}</span>
          {!!row.sku && <span className='eh-bom-node-sku'>{row.sku}</span>}
          <TierBadge value={plan.tiers[node.index]} />
          {plan.inferred[node.index] && (
            <span
              className='eh-bom-node-inferred'
              title='The Tier column was blank — set from the structure.'
            >
              inferred
            </span>
          )}
          {!!node.quantity && (
            <span className='eh-struct-qty'>&times; {node.quantity}</span>
          )}
        </div>
        {node.children.map((c) => renderPlanNode(c, depth + 1, budget))}
      </React.Fragment>
    );
  };

  return (
    <>
      <Modal open={open} onClose={handleClose} className='eh-modal-root'>
      <div
        className='eh-modal eh-modal-wide eh-modal-wizard'
        role='dialog'
        aria-label='Create Database'
      >
        <div className='eh-modal-head'>
          <h2 className='eh-modal-title'>Create Database</h2>
          <button
            className='eh-modal-close'
            onClick={handleClose}
            aria-label='Close'
            type='button'
          >
            <CloseIcon style={{ fontSize: 20 }} />
          </button>
        </div>

        <div className='eh-stepper'>
          {steps.map((label, idx) => (
            <React.Fragment key={label}>
              <div
                className={`eh-step ${idx === step ? 'active' : ''} ${
                  idx < step ? 'done' : ''
                }`}
              >
                <span className='eh-step-dot'>
                  {idx < step ? <CheckIcon style={{ fontSize: 14 }} /> : idx + 1}
                </span>
                <span className='eh-step-label'>{label}</span>
              </div>
              {idx < steps.length - 1 && (
                <span className={`eh-step-line ${idx < step ? 'done' : ''}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        <div className='eh-modal-body'>
          {/* Step 1 — Details */}
          {current === 'Details' && (
            <>
              <label className='eh-field'>
                <span className='eh-field-label'>
                  Name <span className='eh-req'>*</span>
                </span>
                <input
                  className='eh-input'
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder='e.g. Aerospace Components'
                  maxLength={200}
                  autoFocus
                />
              </label>
              <label className='eh-field'>
                <span className='eh-field-label'>Description</span>
                <textarea
                  className='eh-input eh-textarea'
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder='What this database tracks'
                  rows={2}
                  maxLength={2000}
                />
              </label>
              <p className='eh-field-hint'>
                Databases are shared with everyone on your team.
              </p>
            </>
          )}

          {/* Step 2 — Appearance */}
          {current === 'Appearance' && (
            <>
              <div className='eh-field'>
                <span className='eh-field-label'>Icon</span>
                <div className='eh-icon-grid'>
                  {DATABASE_ICON_KEYS.map((key) => {
                    const Ic = getDatabaseIcon(key);
                    const active = icon === key;
                    return (
                      <button
                        key={key}
                        type='button'
                        className={`eh-icon-btn ${active ? 'active' : ''}`}
                        style={
                          active
                            ? { color: accent, borderColor: accent }
                            : undefined
                        }
                        onClick={() => setIcon(key)}
                        aria-label={key}
                      >
                        <Ic style={{ fontSize: 20 }} />
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className='eh-field'>
                <span className='eh-field-label'>Accent color</span>
                <div className='eh-swatch-row'>
                  {ACCENT_PALETTE.map((c) => (
                    <button
                      key={c}
                      type='button'
                      className={`eh-swatch ${accent === c ? 'active' : ''}`}
                      style={{ background: c }}
                      onClick={() => setAccent(c)}
                      aria-label={`Accent ${c}`}
                    />
                  ))}
                </div>
              </div>
              {/* Live preview of the database card chrome. */}
              <div className='eh-field'>
                <span className='eh-field-label'>Preview</span>
                <div className='eh-db-preview'>
                  <span
                    className='eh-db-preview-icon'
                    style={{
                      color: accent,
                      background: `${accent}22`,
                    }}
                  >
                    {React.createElement(getDatabaseIcon(icon), {
                      style: { fontSize: 22 },
                    })}
                  </span>
                  <span className='eh-db-preview-name'>
                    {name.trim() || 'Database name'}
                  </span>
                </div>
              </div>
            </>
          )}

          {/* Step 3 — How products are stored + start mode */}
          {current === 'Products' && (
            <>
              <div className='eh-field'>
                <span className='eh-field-label'>How products are stored</span>
                <div className='eh-store-cards'>
                  <div className='eh-store-card'>
                    <div className='eh-store-card-title'>
                      <AccountTreeOutlinedIcon style={{ fontSize: 16 }} />
                      Hierarchy (BOM)
                    </div>
                    <div className='eh-store-flow'>
                      <span className='eh-store-tier'>Assembly</span>
                      <span className='eh-store-arrow'>→</span>
                      <span className='eh-store-tier'>Sub-assembly</span>
                      <span className='eh-store-arrow'>→</span>
                      <span className='eh-store-tier'>Component</span>
                    </div>
                    <p className='eh-store-card-body'>
                      Each product carries a <strong>tier</strong>, and the
                      import template has a{' '}
                      <code className='eh-code'>parent_sku</code> column: point a
                      row at the SKU of the assembly it belongs to and the
                      parent/child links are built as the products land &mdash;
                      you preview the whole tree before anything is created.
                      Links can also be added later in the database&rsquo;s
                      Hierarchy view.
                    </p>
                  </div>
                  <div className='eh-store-card'>
                    <div className='eh-store-card-title'>
                      <ViewListOutlinedIcon style={{ fontSize: 16 }} />
                      Flat list
                    </div>
                    <p className='eh-store-card-body'>
                      Prefer a simple catalog? Leave{' '}
                      <code className='eh-code'>tier</code> and{' '}
                      <code className='eh-code'>parent_sku</code> blank &mdash;
                      every product is tracked, screened, and licensed on its
                      own.
                    </p>
                  </div>
                </div>
              </div>

              <div className='eh-field'>
                <span className='eh-field-label'>How do you want to start?</span>
                <div className='eh-source-grid eh-source-grid-2'>
                  <button
                    type='button'
                    className={`eh-source-card ${
                      startMode === 'manual' ? 'active' : ''
                    }`}
                    onClick={() => setStartMode('manual')}
                  >
                    <span className='eh-source-label'>Start empty</span>
                    <span className='eh-source-sub'>
                      Create the database, then add products by hand.
                    </span>
                  </button>
                  <button
                    type='button'
                    className={`eh-source-card ${
                      startMode === 'file' ? 'active' : ''
                    }`}
                    onClick={() => setStartMode('file')}
                  >
                    <span className='eh-source-label'>Import from CSV</span>
                    <span className='eh-source-sub'>
                      Bulk-load products from a spreadsheet on the next steps.
                    </span>
                  </button>
                  {/* Upload documents — hidden from Create Database modal.
                  <button
                    type='button'
                    className={`eh-source-card ${
                      startMode === 'documents' ? 'active' : ''
                    }`}
                    onClick={() => setStartMode('documents')}
                  >
                    <span className='eh-source-label'>Upload documents</span>
                    <span className='eh-source-sub'>
                      Parse customs entry PDFs and send the results here.
                    </span>
                  </button>
                  */}
                </div>
              </div>
            </>
          )}

          {current === 'Documents' && (
            <div className='eh-field'>
              <span className='eh-field-label'>Attach a customs entry PDF</span>
              <div
                role='button'
                tabIndex={0}
                onClick={() => documentFileInputRef.current?.click()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ')
                    documentFileInputRef.current?.click();
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDocumentDragActive(true);
                }}
                onDragLeave={() => setDocumentDragActive(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDocumentDragActive(false);
                  const dropped = e.dataTransfer.files?.[0];
                  if (dropped) setDocumentFile(dropped);
                }}
                className={`eh-source-card${documentDragActive ? ' active' : ''}`}
                style={{
                  alignItems: 'center',
                  textAlign: 'center',
                  cursor: 'pointer',
                  padding: '32px 16px',
                }}
              >
                {documentFile ? (
                  <>
                    <span className='eh-source-label'>{documentFile.name}</span>
                    <button
                      type='button'
                      className='btn-outline'
                      style={{ marginTop: 12 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDocumentFile(null);
                      }}
                    >
                      Remove
                    </button>
                  </>
                ) : (
                  <>
                    <span className='eh-source-label'>
                      Click to attach a customs entry PDF
                    </span>
                    <span className='eh-source-sub'>or drag and drop</span>
                  </>
                )}
                <input
                  ref={documentFileInputRef}
                  type='file'
                  accept='application/pdf,.pdf'
                  style={{ display: 'none' }}
                  onChange={(e) =>
                    e.target.files?.[0] && setDocumentFile(e.target.files[0])
                  }
                />
              </div>
              <p className='eh-source-sub' style={{ marginTop: 8 }}>
                You&apos;ll pick how the PDF is analyzed (package check, entry
                parsing, or full comparison) on the next page, right after
                this database is created.
              </p>
            </div>
          )}

          {/* CSV template download + upload */}
          {current === 'Template' && (
            <>
              <div className='eh-field'>
                <span className='eh-field-label'>1. Download the template</span>
                <p className='eh-field-hint'>
                  The template has one column per product field we store, plus{' '}
                  <code className='eh-code'>parent_sku</code> and{' '}
                  <code className='eh-code'>bom_quantity</code> for the
                  hierarchy. Map <code className='eh-code'>parent_sku</code> to a
                  row&apos;s own <code className='eh-code'>internal_sku</code> to
                  wire an assembly&nbsp;&rarr;&nbsp;sub-assembly&nbsp;&rarr;&nbsp;component
                  tree as the rows import.
                </p>
                <button
                  type='button'
                  className='btn-outline'
                  style={{ alignSelf: 'flex-start' }}
                  onClick={() =>
                    downloadCsv(
                      'export-hub-products-template.csv',
                      PRODUCT_TEMPLATE_COLUMNS,
                      PRODUCT_TEMPLATE_EXAMPLE_ROWS,
                    )
                  }
                >
                  <FileDownloadOutlinedIcon style={{ fontSize: 18 }} /> Download
                  CSV Template
                </button>
              </div>

              <div className='eh-field'>
                <span className='eh-field-label'>2. Upload your file</span>
                <div
                  {...getRootProps()}
                  className={`eh-dropzone ${isDragActive ? 'active' : ''} ${
                    isDragReject ? 'reject' : ''
                  } ${file ? 'has-file' : ''}`}
                >
                  <input {...getInputProps()} />
                  {file ? (
                    <div className='eh-dropzone-inner'>
                      <CheckIcon
                        style={{ fontSize: 22, color: 'var(--full-bar-color)' }}
                      />
                      <span className='eh-dropzone-file'>{file.name}</span>
                      <span className='eh-dropzone-sub'>
                        {rawRows.length} row{rawRows.length === 1 ? '' : 's'} ·
                        click to replace
                      </span>
                    </div>
                  ) : (
                    <div className='eh-dropzone-inner'>
                      <span className='eh-dropzone-text'>
                        <strong>Click to upload</strong> or drag &amp; drop
                      </span>
                      <span className='eh-dropzone-sub'>CSV or XLSX</span>
                      {isDragReject && (
                        <span className='eh-dropzone-err'>
                          Unsupported file type.
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Header matching */}
          {current === 'Match' && (
            <>
              <p className='eh-field-hint' style={{ marginBottom: 4 }}>
                Exact matches are auto-selected. Map each column from your file
                to a product field, or leave it as <em>Not mapped</em>. Map{' '}
                <code className='eh-code'>parent_sku</code> to build the product
                hierarchy as the rows import &mdash; the next step previews it.{' '}
                <strong>{mappedCount}</strong> of {headers.length} columns mapped
                · <strong>{productRowCount}</strong> product
                {productRowCount === 1 ? '' : 's'} will be imported.
              </p>
              <div className='eh-map-list'>
                {headers.map((h) => (
                  <div className='eh-map-row' key={h}>
                    <span className='eh-map-src' title={h}>
                      {h}
                    </span>
                    <span className='eh-map-arrow'>→</span>
                    <StyledSingleSelect
                      className='eh-map-styled-select'
                      options={[
                        { value: NOT_MAPPED, label: 'Not mapped' },
                        ...PRODUCT_TEMPLATE_COLUMNS.map((c) => ({
                          value: c,
                          label: c,
                        })),
                      ]}
                      value={mappings[h] || NOT_MAPPED}
                      onChange={(v) => setMappings((m) => ({ ...m, [h]: v }))}
                      inline
                    />
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Structure — the tree the file's parent_sku column describes */}
          {current === 'Structure' && plan && (
            <>
              {!parentMapped ? (
                <div className='eh-bom-empty'>
                  <AccountTreeOutlinedIcon
                    style={{ fontSize: 28, color: 'var(--text-tertiary)' }}
                  />
                  <span className='eh-bom-empty-title'>
                    This file has no hierarchy
                  </span>
                  <p className='eh-bom-empty-body'>
                    To pre-define the tree, add a{' '}
                    <code className='eh-code'>parent_sku</code> column and put
                    each row&rsquo;s parent SKU (or parent name) in it, then map
                    it on the Match step &mdash; the downloadable template
                    already has the column and a worked example. Optionally add{' '}
                    <code className='eh-code'>bom_quantity</code> for how many go
                    into that parent.
                  </p>
                  <p className='eh-bom-empty-body'>
                    Without it the products import as a flat list &mdash; you can
                    still link them later in the database&rsquo;s Hierarchy view.
                  </p>
                </div>
              ) : (
                <>
                  <p className='eh-field-hint' style={{ marginBottom: 4 }}>
                    Built from your <code className='eh-code'>parent_sku</code>{' '}
                    column. <strong>{plan.linkCount}</strong> link
                    {plan.linkCount === 1 ? '' : 's'} across{' '}
                    <strong>{plan.roots.length}</strong> top-level assembl
                    {plan.roots.length === 1 ? 'y' : 'ies'} ·{' '}
                    <strong>{plan.standalone.length}</strong> product
                    {plan.standalone.length === 1 ? '' : 's'} with no parent or
                    children. A blank <code className='eh-code'>tier</code> is
                    set from the structure.
                  </p>

                  {plan.roots.length === 0 ? (
                    <div className='eh-bom-empty'>
                      <span className='eh-bom-empty-title'>
                        No links resolved
                      </span>
                      <p className='eh-bom-empty-body'>
                        The <code className='eh-code'>parent_sku</code> column is
                        mapped but nothing in it matched another row&rsquo;s SKU
                        or name.
                      </p>
                    </div>
                  ) : (
                    <div className='eh-bom-tree'>
                      {(() => {
                        // One budget shared across every root's traversal, so a
                        // single deep tree can't blow past it.
                        const budget = { left: TREE_PREVIEW_NODE_BUDGET };
                        return plan.roots
                          .slice(0, TREE_PREVIEW_CAP)
                          .map((root) => (
                            <div className='eh-bom-tree-root' key={root.index}>
                              {renderPlanNode(root, 0, budget)}
                            </div>
                          ));
                      })()}
                      {(plan.roots.length > TREE_PREVIEW_CAP ||
                        previewNodeCount > TREE_PREVIEW_NODE_BUDGET) && (
                        <div className='eh-bom-more'>
                          Preview truncated — {plan.linkCount} link
                          {plan.linkCount === 1 ? '' : 's'} across{' '}
                          {plan.roots.length} top-level assembl
                          {plan.roots.length === 1 ? 'y' : 'ies'} will still be
                          created.
                        </div>
                      )}
                    </div>
                  )}

                  {plan.issues.length > 0 && (
                    <div className='eh-field'>
                      <span className='eh-field-label'>
                        Rows that won&rsquo;t link ({plan.issues.length})
                      </span>
                      <p className='eh-field-hint'>
                        These products still import &mdash; only the link is
                        skipped.
                      </p>
                      <div className='eh-bom-issues'>
                        {plan.issues
                          .slice(0, ISSUE_PREVIEW_CAP)
                          .map((issue, i) => (
                            <div
                              className='eh-bom-issue'
                              key={`${issue.rowNumber}-${i}`}
                            >
                              <span className='eh-bom-issue-row'>
                                Row {issue.rowNumber}
                              </span>
                              <span className='eh-bom-issue-text'>
                                <strong>{issue.name}</strong> &mdash;{' '}
                                {issue.message}
                              </span>
                            </div>
                          ))}
                        {plan.issues.length > ISSUE_PREVIEW_CAP && (
                          <div className='eh-bom-more'>
                            + {plan.issues.length - ISSUE_PREVIEW_CAP} more
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* Screening settings — last step in both branches */}
          {current === 'Settings' && (
            <>
              <p className='eh-field-hint' style={{ marginBottom: 4 }}>
                Choose how this database is screened. You can change any of this
                later from the database dashboard.
              </p>
              <ScreeningSettingsFields
                frequency={frequency}
                setFrequency={setFrequency}
                dayOfWeek={dayOfWeek}
                setDayOfWeek={setDayOfWeek}
                method={notifyMethod}
                setMethod={setNotifyMethod}
                email={notifyEmail}
                setEmail={setNotifyEmail}
                lenses={lenses}
                setLenses={setLenses}
                availableLenses={availableLenses}
                lensesLoading={lensesLoading}
              />
            </>
          )}
        </div>

        <div className='eh-modal-foot'>
          {step > 0 ? (
            <button
              className='btn-outline'
              type='button'
              onClick={() => setStep((s) => s - 1)}
              disabled={saving}
            >
              Back
            </button>
          ) : (
            <button
              className='btn-outline'
              type='button'
              onClick={handleClose}
              disabled={saving}
            >
              Cancel
            </button>
          )}
          <span className='eh-wizard-stepcount'>
            {saving && importMsg
              ? importMsg
              : `Step ${step + 1} of ${steps.length}`}
          </span>
          {step < lastStep ? (
            <button className='btn-primary' type='button' onClick={next}>
              Continue
            </button>
          ) : (
            <button
              className='btn-primary'
              type='button'
              onClick={handleSubmit}
              disabled={saving || !name.trim()}
              style={{ opacity: saving || !name.trim() ? 0.6 : 1 }}
            >
              {saving
                ? 'Creating…'
                : startMode === 'file'
                ? 'Create & Import'
                : 'Create Database'}
            </button>
          )}
        </div>
      </div>
      </Modal>
      <ProductCreditsModal
        check={creditBlock}
        teamId={groupId || ''}
        onClose={() => setCreditBlock(null)}
      />
    </>
  );
};

export default CreateDatabaseModal;
